/**
 * 기능 ID : COMVRF000-00MS.tsx
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useBackKeyInterceptor, useGlobalErrorStore } from 'scbk-services';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'scbk-ui-web';
import { useTokensStore } from '../Tokens';
import AdditionalARSComponent from './components/AdditionalARSComponent';
import AdditionalCompleteComponent from './components/AdditionalCompleteComponent';
import AdditionalIdVerificationComponent from './components/AdditionalIdVerificationComponent';
import AdditionalIdentifyComponent from './components/AdditionalIdentifyComponent';
import AdditionalIntroComponent from './components/AdditionalIntroComponent';
import AdditionalOverseasDepartureComponent from './components/AdditionalOverseasDepartureComponent';
import AdditionalSimpleComponent from './components/AdditionalSimpleComponent';
import { ADDITIONAL_TYPE } from './enums/additionalTypeEnum';
import { useAutVrfAddActivate } from './hooks/api/autVrfAdditionalHooks';
import { useAdditionalStore } from './stores/useAdditionalStore';
import { AutVrfAddActivateResponse } from './types/api/autVrfAddActivate';

interface Props {
  mode?: 'select' | 'tab';
  authType?: string;
  transType?: string;
  onComplete?: () => void;
  isLoading?: boolean;
  isTokens?: boolean;
}

const AdditionalComponent: React.FC<Props> = ({
  mode = 'select',
  authType,
  transType,
  onComplete,
  isLoading = false,
  isTokens = false,
}) => {
  const [tabSize, setTabSize] = useState(0);
  const [initMode, setInitMode] = useState('');
  const [isIdVerification, setIsIdVerification] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  // 다국어 처리
  const { t } = useTranslation();
  const { getContext: appContext } = useAppContext();

  const {
    activateInfo,
    setActivateInfo,
    step,
    setStep,
    subStep,
    setSubStep,
    reset,
    setSelectedARSTelNo,
    currentAdditionalType,
    setCurrentAdditionalType,
    isSimple,
    isIdentify,
    isARS,
    isOverseasDeparture,
  } = useAdditionalStore();

  const { changeSmsAuthYn } = useTokensStore();

  //뒤로가기
  const backKeyInterceptor = async () => {
    if (subStep > 0) {
      setSelectedARSTelNo(null);
      setSubStep((prev) => Math.max(0, prev - 1));
    } else if (step > 0) {
      setStep((prev) => Math.max(0, prev - 1));
    }

    return step + subStep <= 1;
  };
  useBackKeyInterceptor({ enabled: true }, backKeyInterceptor);

  const { request: activate } = useAutVrfAddActivate({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const execActivate = useCallback(async () => {
    const result: AutVrfAddActivateResponse | undefined = await activate({
      additionalType: authType,
      transType,
    });

    if (!result) return;

    setActivateInfo(result);
    initDisplay(result);
  }, [activate]);

  const initDisplay = useCallback(
    (result: AutVrfAddActivateResponse) => {
      if (!result) return;

      if (result.additionalType === 'P') {
        setIsIdVerification(true);
      } else {
        const filteredAdditionalType = result.additionalType
          .split(':')
          .filter((item: string) => item !== '');

        if (filteredAdditionalType.length > 4) {
          const chkMode = mode || 'tab';
          setInitMode(chkMode);
          setCurrentAdditionalType(filteredAdditionalType[0]);
          setTabSize(chkMode === 'tab' ? filteredAdditionalType.length : 0);
          setStep(chkMode === 'tab' ? 2 : 1);
        } else if (filteredAdditionalType.length > 1) {
          const chkMode = mode || 'select';
          setInitMode(chkMode);
          setCurrentAdditionalType(filteredAdditionalType[0]);
          setTabSize(chkMode === 'tab' ? filteredAdditionalType.length : 0);
          setStep(chkMode === 'tab' ? 2 : 1);
        } else if (filteredAdditionalType.length === 1) {
          if (
            appContext().channel === 'IB' &&
            (result.transType?.includes('TRAN') || result.transType?.includes('CERT'))
          ) {
            setInitMode('select');
            setCurrentAdditionalType(filteredAdditionalType[0]);
            setTabSize(0);
            setStep(1);
          } else {
            setInitMode('tab');
            setCurrentAdditionalType(filteredAdditionalType[0]);
            setTabSize(0);
            setStep(2);
          }
        } else {
          //추가인증 수단이 없을 경우 PASS
          onComplete?.();
          return;
        }
      }
    },
    [activateInfo, step, initMode],
  );

  useEffect(() => {
    console.log(authType);
    reset();
    execActivate();
  }, [changeSmsAuthYn]);

  return (
    <>
      {/* FDS 출금정지 상태 일경우 비대면실명인증 */}
      {isIdVerification && !isComplete && <AdditionalIdVerificationComponent />}

      {initMode === 'select' && step === 1 && !isComplete && (
        <AdditionalIntroComponent
          onComplete={() => {
            setIsComplete(true);
            onComplete?.();
          }}
        />
      )}

      {(initMode === 'tab' || step === 2) && !isComplete && (
        <Tabs value={currentAdditionalType}>
          <TabsList variant="segment" className={tabSize > 0 ? '' : 'hidden'}>
            <TabsTrigger
              value={ADDITIONAL_TYPE.SIMPLE}
              variant="segment"
              className={isSimple() ? '' : 'hidden'}
              onClick={() => {
                setSubStep(0);
                setCurrentAdditionalType(ADDITIONAL_TYPE.SIMPLE);
              }}
            >
              {t('AdditionalComponent.001', '간편인증')}
            </TabsTrigger>
            <TabsTrigger
              value={ADDITIONAL_TYPE.IDENTIFY}
              variant="segment"
              className={isIdentify() ? '' : 'hidden'}
              onClick={() => {
                setSubStep(0);
                setCurrentAdditionalType(ADDITIONAL_TYPE.IDENTIFY);
              }}
            >
              {t('AdditionalComponent.002', '휴대폰인증')}
            </TabsTrigger>
            <TabsTrigger
              value={ADDITIONAL_TYPE.ARS}
              variant="segment"
              className={isARS() ? '' : 'hidden'}
              onClick={() => {
                setSubStep(0);
                setCurrentAdditionalType(ADDITIONAL_TYPE.ARS);
              }}
            >
              {t('AdditionalComponent.003', `전화(ARS인증)`)}
            </TabsTrigger>
            <TabsTrigger
              value={ADDITIONAL_TYPE.OVERSEAS_DEPARTURE}
              variant="segment"
              className={isOverseasDeparture() ? '' : 'hidden'}
              onClick={() => {
                setSubStep(0);
                setCurrentAdditionalType(ADDITIONAL_TYPE.OVERSEAS_DEPARTURE);
              }}
            >
              {t('AdditionalComponent.004', `해외체류인증`)}
            </TabsTrigger>
          </TabsList>
          <TabsContent value={ADDITIONAL_TYPE.SIMPLE}>
            <AdditionalSimpleComponent
              onComplete={() => {
                setIsComplete(true);
                onComplete?.();
              }}
              isLoading={isLoading}
            />
          </TabsContent>
          <TabsContent value={ADDITIONAL_TYPE.IDENTIFY}>
            <AdditionalIdentifyComponent
              onComplete={() => {
                setIsComplete(true);
                onComplete?.();
              }}
              isLoading={isLoading}
            />
          </TabsContent>
          <TabsContent value={ADDITIONAL_TYPE.ARS}>
            <AdditionalARSComponent
              onComplete={() => {
                setIsComplete(true);
                onComplete?.();
              }}
              transType={transType ?? ''}
              isLoading={isLoading}
            />
          </TabsContent>
          <TabsContent value={ADDITIONAL_TYPE.OVERSEAS_DEPARTURE}>
            <AdditionalOverseasDepartureComponent
              onComplete={() => {
                setIsComplete(true);
                onComplete?.();
              }}
              isLoading={isLoading}
            />
          </TabsContent>
        </Tabs>
      )}

      {!isTokens && isComplete && (
        <AdditionalCompleteComponent
          onComplete={() => {
            onComplete?.();
          }}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AdditionalComponent;
